import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { CotizacionRepository } from '../../../domain/repositories/cotizacion/cotizacion.repository';
import {
  CotizacionAsset,
  CotizacionCoverageGroupItem,
  CotizacionMoney,
  CotizacionPlan,
  CotizacionResult,
  CreateCotizacionInput,
} from '../../../domain/interfaces/cotizacion/cotizacion.interface';
import { HttpClientService } from '../../services/http-client/http-client.service';
import { API_ENDPOINTS } from '../../config/api.config';

interface CreateCotizacionRequestDto {
  selected_plan: string;
  lead_id: string;
  payment_frequency: 'Annually' | 'Monthly';
}

interface CotizacionMoneyDto {
  currency?: string;
  amount?: number;
  formatted?: string;
}

interface CotizacionOwnerDto {
  fullname?: string;
  document?: { type?: string; number?: string };
  address?: { country?: string; department?: string; city?: string; street?: string };
}

interface CotizacionAssetDto {
  brand?: string;
  line?: string;
  model?: string;
  cylinder?: string;
  plate?: string;
  owner?: CotizacionOwnerDto;
}

interface CotizacionCoverageGroupItemDto {
  name?: string;
  description?: string;
  include?: boolean;
}

interface CotizacionPlanDto {
  price?: { full?: CotizacionMoneyDto; price_monthly?: CotizacionMoneyDto };
  sumInsured?: CotizacionMoneyDto | null;
  // El doc documenta este campo con shape inconsistente entre ejemplos.
  selected?: string | { Selected?: string };
  coveragesGroup?: CotizacionCoverageGroupItemDto[];
  effectiveDate?: string;
  paymentFrecuency?: string;
  zonaCirculacion?: string;
}

interface CotizacionResponseDataDto {
  quote_id: string;
  pre_policy_number?: string;
  zonaCirculacion?: string;
  asset?: CotizacionAssetDto;
  plan?: CotizacionPlanDto;
}

@Injectable()
export class CotizacionRepositoryImplementation extends CotizacionRepository {
  private readonly http = inject(HttpClientService);

  override create(input: CreateCotizacionInput): Observable<CotizacionResult> {
    const dto: CreateCotizacionRequestDto = {
      selected_plan: input.selectedPlan,
      lead_id: input.leadId,
      payment_frequency: input.paymentFrequency,
    };

    return this.http
      .post<CotizacionResponseDataDto>(API_ENDPOINTS.cotizacion.create, dto)
      .pipe(map((response) => this.toResult(response.data)));
  }

  override getByQuoteId(quoteId: string): Observable<CotizacionResult> {
    return this.http
      .get<CotizacionResponseDataDto>(API_ENDPOINTS.cotizacion.getById(quoteId))
      .pipe(map((response) => this.toResult(response.data)));
  }

  private toResult(dto: CotizacionResponseDataDto): CotizacionResult {
    return {
      quoteId: dto.quote_id,
      prePolicyNumber: dto.pre_policy_number ?? '',
      circulationZone: dto.zonaCirculacion ?? '',
      asset: this.toAsset(dto.asset),
      plan: this.toPlan(dto.plan),
    };
  }

  private toAsset(asset: CotizacionAssetDto | undefined): CotizacionAsset {
    const owner = asset?.owner;
    return {
      brand: asset?.brand ?? '',
      line: asset?.line ?? '',
      model: asset?.model ?? '',
      cylinder: asset?.cylinder ?? '',
      plate: asset?.plate ?? '',
      owner: {
        fullName: owner?.fullname ?? '',
        documentType: owner?.document?.type ?? '',
        documentNumber: owner?.document?.number ?? '',
        country: owner?.address?.country ?? '',
        department: owner?.address?.department ?? '',
        city: owner?.address?.city ?? '',
        street: owner?.address?.street ?? '',
      },
    };
  }

  private toPlan(plan: CotizacionPlanDto | undefined): CotizacionPlan {
    return {
      priceFull: this.toMoney(plan?.price?.full),
      priceMonthly: plan?.price?.price_monthly ? this.toMoney(plan.price.price_monthly) : null,
      sumInsured: plan?.sumInsured ? this.toMoney(plan.sumInsured) : null,
      selected: this.toSelectedPlanCode(plan?.selected),
      coveragesGroup: (plan?.coveragesGroup ?? []).map((item) => this.toCoverageGroupItem(item)),
      effectiveDate: plan?.effectiveDate ?? '',
      paymentFrequency: plan?.paymentFrecuency ?? '',
      circulationZone: plan?.zonaCirculacion ?? '',
    };
  }

  private toSelectedPlanCode(selected: string | { Selected?: string } | undefined): string {
    if (!selected) return '';
    return typeof selected === 'string' ? selected : (selected.Selected ?? '');
  }

  private toCoverageGroupItem(item: CotizacionCoverageGroupItemDto): CotizacionCoverageGroupItem {
    return { name: item.name ?? '', description: item.description ?? '', include: item.include ?? false };
  }

  private toMoney(money: CotizacionMoneyDto | undefined): CotizacionMoney {
    if (!money) return { currency: 'COP', amount: 0, formatted: '' };
    return { currency: money.currency ?? 'COP', amount: money.amount ?? 0, formatted: money.formatted ?? '' };
  }
}
